import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  id_token: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  // Configuration from environment
  private readonly authBaseUrl = environment.extAuthUrl;
  private readonly clientId = environment.oidc.clientId;
  private readonly clientSecret = environment.oidc.clientSecret;
  private readonly redirectUri = environment.oidc.redirectUri;
  private readonly scope = environment.oidc.scope;
  private readonly extAuthToken = (environment as any).extAuthToken;

  // Storage keys
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly ID_TOKEN_KEY = 'id_token';
  private readonly USER_INFO_KEY = 'user_info';

  /**
   * Generate authorization URL for standard login
   */
  getAuthorizationUrl(): string {
    const state = this.generateState();
    const nonce = this.generateNonce();

    // Store state and nonce for validation
    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_nonce', nonce);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: this.scope,
      state: state,
      nonce: nonce,
    });

    return `${this.authBaseUrl}/ext-auth/authorize?${params.toString()}`;
  }

  /**
   * Submit login credentials via POST to authorize endpoint
   * Note: Backend only validates username, password is kept for frontend validation
   */
  async submitLoginCredentials(username: string, password: string): Promise<void> {
    const state = this.generateState();
    const nonce = this.generateNonce();

    // Store state and nonce for validation
    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_nonce', nonce);
    sessionStorage.setItem('debug_login_attempt', new Date().toISOString());

    // Create form data for POST request (backend only expects username, not password)
    const formData = new URLSearchParams({
      response_type: 'code',
      username: username,
      password: password,
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: this.scope,
      state: state,
    });

    // Create a form and submit it (to handle redirect properly)
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${this.authBaseUrl}/ext-auth/authorize-portal`;
    console.log('📡 AuthService: Submitting credentials to', form.action);
    console.log('📡 AuthService: authBaseUrl is', this.authBaseUrl);

    formData.forEach((value, key) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Hybrid Development Login
   * Uses a fixed UUID token to bypass OIDC flow locally
   */
  async devLogin(): Promise<void> {
    if (!this.extAuthToken) {
      throw new Error('No dev auth token configured');
    }

    console.log('🚀 Initiating Hybrid Dev Login with fixed UUID');

    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${this.authBaseUrl}/ext-auth/token/oidc-generate`, {
          auth_token: this.extAuthToken,
        }),
      );

      // Map ext-auth response to OIDC token response format
      const tokenResponse: TokenResponse = {
        access_token: response.access_token,
        id_token: response.id_token, // Use real OIDC ID token from hybrid endpoint
        token_type: 'Bearer',
        expires_in: response.expires_in,
      };

      this.storeTokens(tokenResponse);

      // For dev login, we might not have a full ID token to decode
      // Use a dummy user info or try decoding if it is a JWT
      try {
        this.storeUserInfo(tokenResponse.id_token);
      } catch {
        localStorage.setItem(
          this.USER_INFO_KEY,
          JSON.stringify({
            sub: 'dev-user',
            name: 'Development User',
            email: 'dev@vantage.ch',
          }),
        );
      }

      console.log('✅ Hybrid Dev Login successful');
    } catch (error) {
      console.error('❌ Hybrid Dev Login failed:', error);
      throw error;
    }
  }

  /**
   * Generate authorization URL for Azure AD login
   */
  getAzureAuthorizationUrl(): string {
    const state = this.generateState();

    // Store state for validation
    sessionStorage.setItem('oauth_state', state);

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: this.scope,
      state: state,
    });

    return `${this.authBaseUrl}/ext-auth/azure-login-portal?${params.toString()}`;
  }

  /**
   * Exchange authorization code for tokens
   *
   * @param code - Authorization code from OAuth provider
   * @param receivedState - State parameter from callback URL (for CSRF protection)
   */
  async exchangeCodeForToken(code: string, receivedState?: string): Promise<void> {
    // Validate state (CSRF protection)
    const storedState = sessionStorage.getItem('oauth_state');

    if (!storedState) {
      console.error('No stored state found!');
      sessionStorage.removeItem('oauth_nonce');
      throw new Error('Invalid state: no stored state found');
    }

    // Compare received state with stored state
    if (receivedState && receivedState !== storedState) {
      console.error('State mismatch! Possible CSRF attack.');
      console.error('Expected:', storedState);
      console.error('Received:', receivedState);
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_nonce');
      throw new Error('Invalid state: possible CSRF attack detected');
    }

    // Clean up session storage
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_nonce');

    // Prepare token request
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: this.redirectUri,
    });

    // Create Basic Auth header
    const credentials = btoa(`${this.clientId}:${this.clientSecret}`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Basic ${credentials}`,
    });

    try {
      const response = await firstValueFrom(
        this.http.post<TokenResponse>(`${this.authBaseUrl}/ext-auth/token`, body.toString(), {
          headers,
        }),
      );

      // Store tokens
      this.storeTokens(response);

      // Decode and store user info from ID token
      this.storeUserInfo(response.id_token);
    } catch (error: unknown) {
      console.error('Token exchange failed:', error);

      if (error && typeof error === 'object' && 'status' in error) {
        const httpError = error as {
          status?: number;
          statusText?: string;
          error?: { detail?: string };
          message?: string;
        };
        console.error('Error details:', {
          status: httpError.status,
          statusText: httpError.statusText,
          error: httpError.error,
          message: httpError.message,
        });

        if (httpError.error?.detail) {
          throw new Error(`Token exchange failed: ${httpError.error.detail}`);
        }
        throw new Error(
          `Failed to exchange authorization code for tokens: ${httpError.status || 'Unknown error'}`,
        );
      }

      throw error instanceof Error
        ? error
        : new Error('Failed to exchange authorization code for tokens');
    }
  }

  /**
   * Store tokens in localStorage
   */
  private storeTokens(response: TokenResponse): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, response.access_token);
    localStorage.setItem(this.ID_TOKEN_KEY, response.id_token);
  }

  /**
   * Decode and store user info from ID token
   */
  private storeUserInfo(idToken: string): void {
    try {
      // Decode JWT (without verification - backend already verified it)
      const payload = this.decodeJWT(idToken);
      localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(payload));
    } catch (error) {
      console.error('Failed to decode ID token:', error);
    }
  }

  /**
   * Simple JWT decoder (payload only)
   */
  private decodeJWT(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Get stored ID token
   */
  getIdToken(): string | null {
    return localStorage.getItem(this.ID_TOKEN_KEY);
  }

  /**
   * Get stored user info
   */
  getUserInfo(): any | null {
    const userInfo = localStorage.getItem(this.USER_INFO_KEY);
    return userInfo ? JSON.parse(userInfo) : null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    // Check if token is expired
    try {
      const idToken = this.getIdToken();
      if (!idToken) {
        return false;
      }

      const payload = this.decodeJWT(idToken);
      const now = Math.floor(Date.now() / 1000);

      return payload.exp > now;
    } catch {
      return false;
    }
  }

  /**
   * Logout user
   */
  logout(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.ID_TOKEN_KEY);
    localStorage.removeItem(this.USER_INFO_KEY);
    localStorage.removeItem('portal_role_data'); // Clear role data
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_nonce');
  }

  /**
   * Generate random state for CSRF protection
   */
  private generateState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generate random nonce
   */
  private generateNonce(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
}
